import { memo, useCallback, useEffect, useState } from 'react'
import { User } from 'types/users'
import {
    activateUser,
    blockUser,
    createUser,
    deleteUser,
    editUser,
    getUserById,
    getUsers,
    setUserPassword,
} from 'services/users'
import styles from '../players/index.module.scss'
import { Button, Modal } from 'antd'
import { UsersTable } from '../../components/users-table'
import { UserForm } from '../../components/user-form'
import { UserToRoles } from '../../components/user_to_roles'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

export const Users = memo(() => {
    const [users, setUsers] = useState<User[]>([])
    const [user, setUser] = useState<User>({} as User)
    const [open, setOpen] = useState<boolean>(false)
    const [rolesOpen, setRolesOpen] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getUsers()
        }

        getAll()
            .then(data => {
                setUsers(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const toggleRolesModal = useCallback(() => {
        setRolesOpen(!rolesOpen)
    }, [rolesOpen])

    const handleAdd = useCallback(() => {
        setUser({} as User)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getUserById(id)
                .then(item => {
                    setUser(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleFormSubmit = useCallback(
        (submitValues: User) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            if (user.id) {
                editUser({
                    ...submitValues,
                    id: user.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createUser(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, user, toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteUser(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleBlockUser = useCallback(
        (id: number) => {
            setLoading(true)
            blockUser(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleActivateUser = useCallback(
        (id: number) => {
            setLoading(true)
            activateUser(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleSetUserPassword = useCallback(
        (id: number, password: string) => {
            setLoading(true)
            setUserPassword(id, password)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleShowRoles = useCallback(
        (id: number) => {
            setLoading(true)
            getUserById(id)
                .then(item => {
                    setUser(item)
                    toggleRolesModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleRolesModal]
    )

    const editMode = !!user.id
    const modalTitle = `${editMode ? `Edit user ${user.email}` : `Add new user`}`

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Users
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <UsersTable
                data={users}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={somethingLoading}
                onBlock={handleBlockUser}
                onActivate={handleActivateUser}
                onSetUserPassword={handleSetUserPassword}
                onShowRoles={handleShowRoles}
            />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <UserForm data={user} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
            <Modal
                title={formatModalTitle('Roles', user.name || user.email)}
                centered
                open={rolesOpen}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleRolesModal}
                maskClosable={false}
            >
                <UserToRoles user={user} onSubmit={toggleRolesModal} />
            </Modal>
        </div>
    )
})
